import { useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { useParams } from "react-router-dom";
import { Typography, Stack, Divider, Box } from "@mui/material";
import { useTranslation } from 'react-i18next';
import { useRestaurantData } from '../../../provider/RestaurantDataContext';
import CategoryDto from '../../../models/CategoryDto';
import ItemDto from '../../../models/ItemDto';
import SubCategoryDto from '../../../models/SubCategoryDto';
import { currencyFormat } from '../../../utils/Utils';
import ContextPath from '../contextPath/ContextPath';
import ItemExtras from '../extras/ItemExtras';
import Options from '../options/Options';
import FoodType from '../foodType/FoodType';
import ItemAllergenDisplayer from '../allergen/ItemAllergenDisplayer';
import "./ItemPage.css";
import { useItemContext } from '../../../provider/ItemContext';

const StyledBox = styled(Box)(({ theme }) => ({
  marginRight: '3.125rem',
  marginLeft: '3.438rem',
  marginTop: '0.625rem',
  marginBottom: '2.5rem',
  whiteSpace: 'pre-wrap',
  textAlign: 'center',
  color: 'white',
}));

export default function ItemPage() {
  //TODO überall useEffect wenn .length==0
  const { restaurantData, restaurantData: { currency } } = useRestaurantData();
  const { t } = useTranslation();
  const params = useParams();
  const { setItem } = useItemContext();

  const item = useMemo(() => restaurantData.categories
    .filter((category: CategoryDto) => category.id === Number(params.mainCategory))[0]
    .subCategories.filter((subCategories: SubCategoryDto) => subCategories.items
      .filter((item) => item.id === Number(params.itemDetail)).length === 1)[0].items
    .filter((item: ItemDto) => item.id === Number(params.itemDetail))[0], [params.itemDetail, params.mainCategory, restaurantData.categories]);

  useEffect(() => {
    setItem(item);
    return () => {
      setItem(undefined);
    }
  }, []);

  return (
    <>
      <ContextPath />
      <Stack
        direction="column"
        justifyContent="center"
        style={{ marginBottom: '1.2rem' }}
        spacing={{ xs: 1, sm: 2, md: 2 }}
      >
        {item.imageUrl && item.imageUrl.length > 0 &&
          <img
            src={item.imageUrl}
            alt={item.value.value}
            style={{
              borderRadius: '1.875rem',
              width: "fit-content",
              minWidth: "100%",
              maxWidth: "100%",
              marginLeft: "auto",
              marginRight: "auto",
              marginTop: "2rem",
              marginBottom: item.symbolicImage ? "-0.5rem" : "1rem",
            }}
          />}
        {
          item.symbolicImage &&
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
            <Typography variant="caption" display="block" gutterBottom sx={{ color: 'white', marginRight: '1.5rem', mb: '1rem' }}>
              {t('symbolicImage')}
            </Typography>
          </Box>
        }
        <Box sx={{ background: "#484f60", borderRadius: "1.25rem", pt: '0.5rem' }}>
          <Typography variant="h5" gutterBottom className="center white" sx={{ whiteSpace: 'pre-wrap', hyphens: 'auto' }}>
            {item.value.value}
          </Typography>
          <Divider variant="middle" sx={{ mb: '0.8rem', bgcolor: 'darkgrey' }} />
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Typography
              sx={{ background: "#1976d2", borderRadius: "0.313rem", pr: '0.375rem', pl: '0.313rem' }}
              variant="h6"
              gutterBottom
              className="center white"
            >
              {currencyFormat(item.price) + currency.detail}
            </Typography>
          </Box>
          <Box sx={{ mr: '3.125rem', ml: '3.438rem', mt: '0.625rem', mb: '2.5rem', whiteSpace: 'pre-wrap' }} className='center white' dangerouslySetInnerHTML={{ __html: item.value.description }} />
          <StyledBox>
            <Options displayOnly optionGroups={item.itemOptionGroups} />
          </StyledBox>
          <StyledBox>
            <ItemExtras displayOnly itemExtras={item.itemExtras} />
          </StyledBox>
          {(item.allergens.length > 0 || item.foodTypes.length > 0) && <Divider variant="middle" sx={{ mb: '0.8rem', bgcolor: 'darkgrey' }} />}
          <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
            {
              item.foodTypes.map((foodType) => (
                <FoodType key={foodType.id} foodType={foodType} />
              ))
            }
          </Stack>
          {item.allergens.length > 0 &&
            <ItemAllergenDisplayer allergens={item.allergens} />
          }
        </Box>
      </Stack>
    </>
  );
}